import React, { useState, useContext, useEffect } from 'react'
import ResponsiveAppBar from '../../components/Navbar'
import { getInformation } from '../../services/admin'
import { UserContext } from '../../context/UserProvider.jsx'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Grid,
  Container,
  Paper,
  Button,
  Stack,
  Typography,
} from '@mui/material'
import { CheckCircleOutline, HighlightOff } from '@mui/icons-material'
import { useParams } from 'react-router-dom'

const permissionDescriptions = {
  customers: 'View and manage customers',
  'customer onboarder': 'Create and onboard new customers',
  'customer - sales person reassign': 'Reassign a customer to another sales person',
  'customers - user specific': 'View only customers assigned to this user',
  invoices: 'View and manage invoices',
  orders: 'View and manage orders',
  'orders - user specific': 'View only orders for assigned customers',
  products: 'View and manage products',
  'sales person': 'Show this user in the sales person selection list',
  users: 'View and manage users and permissions',
}

const notificationDescriptions = {
  invoices: 'Receive new invoice email notifications',
  orders: 'Receive order emails when a customer places an order',
  'orders - user specific': 'Receive order emails only when assigned to a customer as the sales person',
}

const groupNames = {
  customer: 'Customers',
  customers: 'Customers',
  invoices: 'Invoices',
  orders: 'Orders',
  products: 'Products',
  'sales person': 'Sales Person',
  users: 'Users',
}

const permissionLabel = (key) => key.toLocaleUpperCase()

const permissionGroup = (key) => {
  const normalizedKey = key.toLocaleLowerCase()
  const groupKey = normalizedKey.startsWith('customer')
    ? 'customers'
    : normalizedKey.startsWith('order')
      ? 'orders'
      : normalizedKey

  return groupNames[groupKey] || 'Other'
}

const App = () => {

  const [userData, setUserData] = useState(null);
  const { token } = useContext(UserContext);
  const { id } = useParams();

  useEffect(() => {
    getInformation(token, id).then((res) => {
      setUserData(res.data)
    }).catch((err) => {
      alert(err.response.data.message);
    });
  }, [id, token])

  if (!userData) {
    return null
  }

  return (
    <>
      <ResponsiveAppBar />
      <Container maxWidth={'xl'}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <h1>User Information</h1>
          </Grid>
          <Grid item xs={8}>
          
          </Grid>
        </Grid>
        <div
          style={{
            height: '80vh',
            width: '100%',
            backgroundColor: '#ffff',
            marginTop: '20px',
          }}
        >
          <Paper elevation={0} sx={{ padding: 3 }}>
           <InformationView
                userData={userData}
              />
          </Paper>
        </div>
      </Container>
    </>
  )
}

const PermissionSummary = ({ list = {}, type = 'access' }) => {
  const descriptions = type === 'notifications'
    ? notificationDescriptions
    : permissionDescriptions
  const groups = Object.keys(list).reduce((result, key) => {
    const group = permissionGroup(key)
    result[group] = [...(result[group] || []), key]
    return result
  }, {})

  return (
    <Stack spacing={2} sx={{ py: 0.5 }}>
      {Object.keys(groups)
        .sort((left, right) => left.localeCompare(right))
        .map((group) => (
          <Box key={group} component="section" aria-label={`${group} ${type}`}>
            <Typography
              component="h3"
              variant="subtitle2"
              sx={{ color: 'text.secondary', mb: 0.75, textTransform: 'uppercase' }}
            >
              {group}
            </Typography>
            <Stack component="ul" spacing={0.75} sx={{ m: 0, p: 0, listStyle: 'none' }}>
              {groups[group]
                .sort((left, right) => permissionLabel(left).localeCompare(permissionLabel(right)))
                .map((key) => {
                  const enabled = Boolean(list[key])
                  const StatusIcon = enabled ? CheckCircleOutline : HighlightOff

                  return (
                    <Box
                      component="li"
                      key={key}
                      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
                    >
                      <StatusIcon
                        aria-hidden="true"
                        fontSize="small"
                        color={enabled ? 'success' : 'error'}
                        sx={{ mt: 0.2, flexShrink: 0 }}
                      />
                      <Box>
                        <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                          {permissionLabel(key)}
                        </Typography>
                        <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
                          {' — '}{descriptions[key] || `Controls ${key.toLocaleLowerCase()} access`}
                        </Typography>
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ ml: 1, color: enabled ? 'success.dark' : 'error.dark', fontWeight: 600 }}
                        >
                          {enabled ? 'Enabled' : 'Disabled'}
                        </Typography>
                      </Box>
                    </Box>
                  )
                })}
            </Stack>
          </Box>
        ))}
    </Stack>
  )
}

const InformationView = ({ userData }) => {
  const { id } = useParams();
  const navigate = useNavigate()

  return (
    <>
      <table className="table-information">
        <tbody>
        <tr>
            <td className="caption">
              <strong>Name: </strong>
            </td>
            <td>{userData['name'] ?? ''}</td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Email Address: </strong>
            </td>
            <td>{userData['email'] ?? ''}</td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Access: </strong>
            </td>
            <td>
              <PermissionSummary list={userData['access'].control} />
            </td>
          </tr>
          <tr>
            <td className="caption">
              <strong>Notification: </strong>
            </td>
            <td>
              <PermissionSummary
                list={userData['access'].notifications}
                type="notifications"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <Button
        type="submit"
        variant="contained"
        color="primary"
        style={{ marginTop: '20px', float: 'right' }}
        onClick={() => {
          navigate(`/users/${id}/update`)
        }}
      >
        Update
      </Button>
      <Button
        type="submit"
        variant="contained"
        color="primary"
        style={{ marginTop: '20px', float: 'right', marginRight: '10px' }}
        onClick={() => {
          navigate(-1)
        }}
      >
        Back
      </Button>
    </>
  )
}

export default App
